import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { BehaviorSubject, Observable, Subject } from '@shared/rx';
import {
  ENTITY_TYPES,
  EntityId,
  ModalAction,
  IModalDialogOptions,
  ObjectId,
  PropertyPredicate,
  ISortOptions,
  IWidgetEvent,
  XUX_GRID_ACTIONS,
  XuxFieldPreferences,
  XuxGridAction,
  IXVIListColumn,
  IToolbarButtonDefinition,
  EPlacement,
  ILabel,
  IBaseEntityType,
} from '@shared/types';
import { WIDGET_COMPONENT } from '@shared/types/enums/widget-component.enum';
import { Condition } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/common.type';
import { Type } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';

export const XUX_FORM_ACTIONS = {
  FORM_READY: 'FORM_READY',
  FORM_VALUE_CHANGED: 'FORM_VALUE_CHANGED',
  FORM_STATUS_CHANGED: 'FORM_STATUS_CHANGED',
  FORM_SELECTED_GROUP_CHANGED: 'FORM_SELECTED_GROUP_CHANGED',
  FORM_EXPAND_CHANGED: 'FORM_EXPAND_CHANGED',
};

export enum FormControlType {
  control = 'FormControl',
  group = 'FormGroup',
}

export type FieldList<F extends string = string> = {
  [key in F]: IFieldPreferences;
};

// can be either function that composes option text from option value, or
// an object with key: value pairs
export type OptionTextProperty =
  | ((val: string) => string)
  | { [fieldValue: string]: string };

export interface IWidgetAction extends XuxGridAction {
  type: XUX_GRID_ACTIONS | any;
  fieldPath?: string;
}

export interface IFormAction<FormValueType = any> extends IWidgetAction {
  selectedGroupIndex?: number;
  formValue?: FormValueType;
}

export interface ICommonFormAction<FormValueType = IBaseEntityType>
  extends IFormAction<FormValueType> {
  formKey: string;
}

/**
 * Defines layout preferences for the form group. It is used to specify the number of columns for field layout in the form group.
 */
export interface IFormLayout {
  /**
   * Number of columns for field layout in the form group. Default is 1. Not applicable for panel widget family.
   */
  columnCount?: number;
}

export interface IFieldPreferences extends XuxFieldPreferences {
  componentClass?: any;
  asyncValidators?: any[];
  class?: string;
  wrapperClass?: string;
  widgetData?: IWidgetData;
  defaultValue?: any;
  deprecationInfo?: string;
  falseValue?: any;
  name?: string;
  titleObs?: BehaviorSubject<string> | Observable<string> | Promise<string>;
  titleTips?: string;
  titleTipsObs?: BehaviorSubject<string> | Observable<string> | Promise<string>;
  descriptionObs?: Observable<string> | Promise<string>;
  disabled?: PropertyPredicate;
  // Hide arrows in input[type=number]
  hideArrows?: boolean;
  /**
   * set control for this node to disabled state, which hides the widget,
   * removes this node from form value and turns off field validation.
   */
  omit?: PropertyPredicate;
  optionText?: OptionTextProperty;
  panelClass?: string | Object;
  itemClass?: string | Object;
  path?: string;
  placeholder?: string;
  properties?: FieldList;
  schema?: {
    [key: string]: any;
  };
  show?: PropertyPredicate;
  text?: string;
  trueValue?: any;
  validators?: any[];
  /**
   * u can pass widgetConstructor or WIDGET_COMPONENT name here to used in dynamic component
   */
  widget?: WIDGET_COMPONENT | Type<any>;
  /**
   * A temporary solution to specify widget family.
   * By default is set to xux or legacy depending on root form container.
   * Required to be set for legacy widgets on the XUX forms and vise versa.
   */
  widgetFamily?: 'xux' | 'legacy';
  action?: Subject<IWidgetAction>;
  eventBus$?: Observable<IWidgetEvent>;
  control?: AbstractControl;
  /**
   * Field path used to distinguish rows by grid widgets
   */
  valueField?: string;
  packageChangesData?: any[];
  /**
   * Angular Control Type: {@link FormGroup} or {@link FormControl}.
   * By default (If the value not specified) form builder makes an attempt to figure
   * it out the value automatically based on the Schema Definition of the field.
   * {@link FormArray} not implemented yet, may be it will be implemented in the future.
   */
  controlType?: FormControlType;
  currentDocumentEntityId?: EntityId;
  entityType?: ENTITY_TYPES;
  /**
   * suppress disable field when no permission
   */
  suppressNoPermissionsDisable?: boolean;

  /**
   * use this to control some fields not need translate
   */
  omitTranslationProperties?: string[];

  /**
   * ignore autoTitle filed when translating
   */
  ignoreAutoTitleWhenTranslate?: boolean;

  layout?: IFormLayout;
}

export interface IFormGroupPreferences {
  id?: string;
  name?: string;
  hidden?: PropertyPredicate;
  disabled?: PropertyPredicate;
  fields?: IFieldPreferences[];
  itemClass?: string;
  hiddenObs?: Observable<boolean>;
  disabledObs?: Observable<boolean>;
  action?: Subject<IWidgetAction>;
  relatedChanges?: any[];
  section_identifier?: string;

  layout?: IFormLayout;
}

export interface IFormGroupRules {
  id?: string;
  name?: string;
  titleObs?: BehaviorSubject<string> | Observable<any> | Promise<any>;
  hidden?: PropertyPredicate;
  disabled?: PropertyPredicate;
  fields?: string[];
  itemClass?: string;
  groupClass?: string;
  section_identifier?: string;

  layout?: IFormLayout;
}

export interface IFormRules<F extends string = string> {
  defaultGroup?: string | boolean;
  virtual?: any;
  groups?: IFormGroupRules[];
  fields?: FieldList<F>;
  validators?: ValidatorFn[];
  bannerText?: string;
}

export interface IWidgetOption {
  autoTitle?: boolean;
  hasControl?: boolean;
}

export interface IWidgetOptions {
  [key: string]: IWidgetOption;
}

export interface IWidgetData {
  /**
   * A generic name that can be applied to any item in the widget's collection.
   * It is used for general purposes and displayed during confirmation operations.
   * e.g. 'Item', 'Alternate code'.
   */
  commonName?: string;

  /**
   * Defines an entity_type.
   * It is used in entity widgets to acquire an entityService,
   * access entity config and API route
   */
  entity?: ENTITY_TYPES;

  /**
   * When lazy is set to true, most of entity widgets should avoid fetching data
   * prior to any user activity.
   */
  lazy?: boolean;

  /**
   * mappers that are used to alter control value while passing to widget and back to the main form
   */
  widgetMappers?: IWidgetMappers;

  /**
   * Entity widgets will search for document in this field before fetching it by entity_id
   * Usually, populated items are provided to this property to avoid excessive API requests
   */
  preloadedItems?: Observable<any[]> | any[];

  /**
   * A DataSource instance used by entity widgets. Has precedence over 'entity' field.
   */
  dataSource?: any;

  /**
   * Options passed to a new dataSource during creation by entity widget.
   * Ignored, when an existing dataSource instance is provided by 'dataSource' field.
   */
  dataSourceOptions?: {
    // this option make control touched and should be used only if control dynamically appear on condition
    defaultSelectedValue?: Condition;
    [key: string]: any;
  };

  multiselect?: boolean;

  filterable?: boolean;

  /**
   * When enableClientFiltering is set to true, user can search options from the dropdown
   * list not send request when the input string length bigger than minClientFilteringCharLength,
   * and minClientFilteringCharLength is set 2 by default.
   */
  enableClientFiltering?: boolean;
  minClientFilteringCharLength?: number;

  /**
   * Turns on indeterminate state in the checkbox-like widgets
   */
  indeterminateSupport?: boolean;

  /**
   * 'control' value for checked state of a checkbox or a switch widget
   */
  trueValue?: any;

  /**
   * 'control' value for unchecked state of a checkbox or a switch widget
   */
  falseValue?: any;

  /**
   * Defines a create button availability on array widgets
   */
  canAdd?: boolean | string;

  /**
   * Defines whether items are editable in an array widget
   */
  canEdit?: boolean;

  /**
   * Defines whether items are removable in an array widget
   */
  canRemove?: boolean;

  /**
   * Defines whether items can be reordered in an array widget
   */
  orderControls?: boolean;

  /**
   * Defines whether selected items can move to the head or the end of an array
   */
  advancedOrderControls?: boolean;

  /**
   * Determines whether ability to create an item
   * and put it before or after the selected item
   */
  canAddInPosition?: boolean;

  /**
   * User defined toolbar buttons passed to widget.
   */
  buttons?: IToolbarButtonDefinition[];

  columnsObs?: Observable<IXVIListColumn[]>;

  columns?: IXVIListColumn[];

  hideEmptyOption?: boolean;

  /**
   * User defined function or string that could be called in widget.
   */
  modalTitle?: ModalTitleFn | string;

  /**
   * Defined the all needed options for rendering for modlas.
   */
  modalOptions?: IModalDialogOptions;

  /**
   * variation widget mode
   * Possible values for now are:
   */
  variationMode?: any;
  variationDocumentMode?: any;

  priceOptions?: any;

  /**
   * Function that should return all needed options for modal confirmation.
   */
  composeConfirmationModalOptions?: (
    action: ModalAction,
    items: any[],
  ) => IModalDialogOptions;

  /**
   * Function that should return generated entity id.
   */
  uniqEntityIdGenerator?: () => ObjectId;

  /**
   * Defines sort options to represent sorted options in widget
   */
  sortOptions?: ISortOptions;

  /**
   * flag for the force refreshCells after view init and GRID_READY
   * it used refreshCellObs observable for the trigger refreshCells
   * if refreshCellObs has "true" value subscription is closed
   */
  forceCellRefresh?: boolean;
  refreshCellObs?: Observable<any> | Promise<any>;

  /**
   * Field title which will used in assign dialog
   * we need this due to difference in titles,
   * some titles should be plural, other single
   */
  assignMessageFieldTitle?: string;

  /**
   * Function that is used for creating subscription on a widget field value changes
   * that we can call at the context an editor
   */
  fieldSubscriptionMapper?: Function;

  /**
   * Flag for reset control value in Select Widget after value was selected
   */
  isNeedToResetControlValue?: boolean;

  /**
   * Flag for hiding cell renderer on document MOMs modal
   * Used in pair with checkValueForEmptinessFn
   */
  hideCellRendererIfEmptyValue?: boolean;

  /**
   * Call back function for indicate value emptiness
   */
  checkValueForEmptinessFn?: (entityId: boolean) => boolean;

  /**
   * When this flag set as true, it's allow to create entity from xux-input-entity-select
   * Used in pair with createModalOptionsFn
   */
  canAddMultiEdit?: boolean;

  /**
   * Call back function for creation entity from xux-input-entity-select
   */
  createModalOptionsFn?: () => IModalDialogOptions;

  /**
   * Call back function for select button in xux-button-select
   */
  selectActionFn?: (value: string) => void;

  /**
   * When this flag set as true, it's submit value in row editing/adding after outside click
   */
  stopEditingWhenGridLosesFocus?: boolean;

  /**
   * Label used for placeholder and empty value (null as a value) for entity-select.component.ts
   */
  variousValueText?: string;

  /**
   * The property says that we need to check whether the entity
   * has dependencies on another entity
   * (if we set up this flag as true we need to set up the "entity" property as well)
   */
  needToCheckDependencies?: boolean;

  /**
   * observable for inlineRemove
   */
  inlineRemoveObs?: Observable<boolean>;

  /**
   * Show row options in the end of each row
   */
  showRowOptions?: boolean;

  /**
   * use the input url for XuxSelectEventWithGridModalComponent
   */
  useInputUrl?: boolean;

  /**
   * max select limit for site selector(need component support)
   */
  maxSelectLimit?: number;

  /**
   * the component is used at isModal
   */
  isModal?: boolean;

  /**
   * only used at xux-edit grid
   * judge is need filter items by filter model before set grid items data
   * if true will filter it
   */
  filterBeforeUpdateItems?: boolean;

  dataObs?: ILabel[] | null;

  rowSelectableWhenDisabled?: boolean;

  [key: string]: any;

  /**
   * Call back function to set the default value when clearing the input and losing focus
   */
  defaultValueByClearFn?: () => number;

  /**
   * for xvi-input-currency, if set false, dot auto padding zero
   */
  autoPaddingZero?: boolean;
  /**
   * for xvi-input-currency, if set true, stop precision validator
   */
  stopPrecisionValidator?: boolean;
  // for label component, info tips for clickable message
  otherTips?: string;
  // for label component，click handler
  clickFn?: Function;
  summaryList?: SummaryListItem[];
  // for tooltip placement
  placement?: EPlacement;
  // for tooltip show or not
  showTooltip?: boolean;
  // Label text for items displayed in the widget, only used when useVisibleItemCountDescription is true
  itemsLabel?: string;
  // When set to true, uses visible item count description with itemsLabel instead of total item count
  useVisibleItemCountDescription?: boolean;
  // Warning validators whose results do not block form submission,
  // but trigger warning labels and grid row highlighting.
  warningValidators?: GridValidatorFn[];
  /**
   * Grid height. Equals to 300px by default.
   */
  gridHeightObs?: Observable<number | string>;
}

export interface SummaryListItem {
  text: string;
  totalCost: string;
  count?: string | number;
  showHoverCls?: boolean;
  showNegativeCls?: boolean;
  showArrowIcon?: boolean;
  showLastDaysBelow?: boolean;
  lastDaysCount?: string | number;
  gotoOtherPage?: Function;
}

export type ModalTitleFn = (items: object[], action: string) => string;

export interface IWidgetMappers {
  /**
   * function that is used to modify a document value representation for the widget
   */
  formToWidgetMapper?: (value: any) => any;
  /**
   * function used to modify value while passing new values from widget to the main form
   */
  widgetToFormMapper?: (value: any) => any;
  [key: string]: any;
}

export interface IFieldValueChangesOptions {
  doNotTrackDisabledControl?: boolean;
  doNotTrackWhenFormIsPending?: boolean;
}

export interface XVIToolbarButtonDefinition extends IToolbarButtonDefinition {
  omitTranslation?: boolean;
  viewGridConfirmOmitFunc?: Function;
  rowClassRules?: {
    [cssClassName: string]: ((params: any) => boolean) | string;
  };
  viewGridConfirmMessage?: string;
  removePreloadEntityItemsFunc?: Function;
  childItems?: XVIToolbarButtonDefinition[];
  template?: SafeHtml;
  icon?: string;
  iconClass?: string;
  route?: string;
  applyManagePermissionToHide?: boolean;
}

export interface GridValidatorFn extends ValidatorFn {
  (control: AbstractControl, gridItems: any[]): ValidationErrors | null;
}
